import * as R from 'ramda';
import $ from 'jquery';

import jqueryToPromise from 'modules/utils/jqueryToPromise';
import { wait } from 'modules/utils/promise';
import { captureException } from 'modules/error-reporting.js';

import {
    postPaymentCoupon,
    postCheckGiftCard,
    postPayWithDebit,
    postPayWithCard,
    getPaymentStatus,
    getPaymentData,
    getStripePaymentData,
    getStripePaymentStatus,
} from 'api/payment';
import { createGiftCardPaymentAnonymous as createGiftCardPaymentAnonymousAPI } from 'api/boutique';

import { postRepayOrder } from 'api/orders';

export const DEBIT_PROVIDERS = [
    'SOFORT',
    'GIROPAY',
    'IDEAL',
    'PAYPAL',
    'PAYCONIQ',
    'SATISPAY',
    'KLARNA',
    'BANCONTACT',
];

export const SUCCEEDED_STATUS = 'SUCCEEDED';
export const FAILED_STATUS = 'FAILED';
export const CREATED_STATUS = 'CREATED';

export const STRIPE_SUCCEEDED_STATUS = 'succeeded';
export const STRIPE_PROCESSING_STATUS = 'processing';

export const NOT_FOUND_ERROR_TYPE = 'not_found';
export const GENERIC_ERROR_TYPE = 'generic';
export const NETWORK_ERROR_TYPE = 'network';
export const REFUSED_BY_THE_BANK_ERROR_TYPE = 'refused_by_the_bank';
export const PAYMENT_EXPIRED_TYPE = 'payment_expired';
export const KLARNA_ERROR_TYPE = 'klarna_error';

const COLOR_DEPTH_VALID_VALUES = [1, 4, 8, 15, 16, 24, 32, 48];

export const convertColorDepth = colorDepth => {
    if (R.includes(colorDepth, COLOR_DEPTH_VALID_VALUES)) {
        return colorDepth;
    }

    if (colorDepth < 1) {
        return COLOR_DEPTH_VALID_VALUES[0];
    }

    return R.pipe(
        R.append(colorDepth),
        R.sort((a, b) => a - b),
        R.indexOf(colorDepth),
        R.dec,
        inferiorIndex => R.view(R.lensIndex(inferiorIndex))(COLOR_DEPTH_VALID_VALUES)
    )(COLOR_DEPTH_VALID_VALUES);
};

/**
 * Mangopay need to receive some browserInfo for
 * the new DSP2 feature.
 */
export const getBrowserInfo = () => {
    if (
        !window ||
        !window.navigator ||
        !window.screen ||
        R.isNil(window.navigator.javaEnabled()) ||
        R.isEmpty(window.navigator.javaEnabled()) ||
        R.isNil(window.navigator.language) ||
        R.isEmpty(window.navigator.language) ||
        R.isNil(window.screen.colorDepth) ||
        R.isEmpty(window.screen.colorDepth) ||
        R.isNil(window.screen.height) ||
        R.isEmpty(window.screen.height) ||
        R.isNil(window.screen.width) ||
        R.isEmpty(window.screen.width) ||
        R.isNil(window.navigator.userAgent) ||
        R.isEmpty(window.navigator.userAgent)
    ) {
        captureException('Browser Infos Incomplete', {
            browserInfo: {
                javaEnabled: window?.navigator?.javaEnabled(),
                language: window?.navigator?.language || window?.navigator?.userLanguage,
                colorDepth: window?.screen?.colorDepth,
                screenHeight: window?.screen?.height,
                screenWidth: window?.screen?.width,
                userAgent: window?.navigator?.userAgent,
            },
        });
    }

    if (!window || !window.navigator || !window.screen) {
        return {};
    }

    return {
        javaEnabled: window.navigator.javaEnabled(),
        language: window.navigator.language || window.navigator.userLanguage,
        colorDepth: convertColorDepth(parseInt(window.screen.colorDepth, 10)),
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        timezoneOffset: new Date().getTimezoneOffset().toString(),
        userAgent: window.navigator.userAgent,
        javascriptEnabled: true,
    };
};

export function fetchPaymentData(paymentUuid) {
    return getPaymentData(paymentUuid, {
        // hack to bypass HTTP cache (@see https://github.com/lrqdo/planning/issues/1384)
        t: Date.now(),
    });
}

export function fetchStripePaymentData(paymentUuid) {
    return getStripePaymentData(paymentUuid, {
        // hack to bypass HTTP cache (@see https://github.com/lrqdo/planning/issues/1384)
        t: Date.now(),
    });
}

const CHECK_POLL_INTERVAL = 3000;

function checkPaymentAndRetryIfNeeded(jqXHR, paymentUuid) {
    const payment = jqXHR.body;

    if (payment.paymentStatus === FAILED_STATUS) {
        const err = new Error(payment.status);
        // @FIXME (sinewyk): figure out proper error wrapping pattern ?
        // @ref https://github.com/joyent/node-verror
        err.data = payment;
        return Promise.reject(err);
    }

    const isValidPayment = payment.paymentStatus === SUCCEEDED_STATUS || payment.secureMode;
    if (isValidPayment) {
        return Promise.resolve(jqXHR);
    }

    return wait(CHECK_POLL_INTERVAL)
        .then(() => fetchPaymentStatus(paymentUuid))
        .then(statusXHR => checkPaymentAndRetryIfNeeded(statusXHR, paymentUuid));
}

export function fetchPaymentStatus(paymentUuid) {
    return getPaymentStatus(paymentUuid, {
        // hack to bypass HTTP cache (@see https://github.com/lrqdo/planning/issues/1384)
        t: Date.now(),
    });
}

export function fetchStripePaymentStatus(paymentUuid) {
    return getStripePaymentStatus(paymentUuid, {
        // hack to bypass HTTP cache (@see https://github.com/lrqdo/planning/issues/1384)
        t: Date.now(),
    });
}

export function payWithRegisteredCard(
    paymentUuid,
    {
        cardId,
        keepCard = false,
        couponAmount = 0,
        giftCardCode,
        charityOrganisationId,
        professionalData,
    }
) {
    const paymentData = {
        card_id: cardId,
        keep_card: keepCard,
        coupon_amount: couponAmount,
        browserInfo: getBrowserInfo(),
    };
    if (giftCardCode) {
        paymentData.gift_card_code = giftCardCode;
    }
    if (charityOrganisationId) {
        paymentData.charity_organisation_id = charityOrganisationId;
    }
    if (professionalData) {
        paymentData.professionalData = professionalData;
    }
    return postPayWithCard(paymentUuid, paymentData).then(
        registerXHR => checkPaymentAndRetryIfNeeded(registerXHR, paymentUuid),
        err => {
            if (err.status === 400 && err.body && err.body.type) {
                err.type = err.body.type;
            }
            return Promise.reject(err);
        }
    );
}

export function payWithDirectDebit(
    paymentUuid,
    { debitProvider, couponAmount = 0, giftCardCode, charityOrganisationId, professionalData }
) {
    const paymentData = {
        method: debitProvider,
        coupon_amount: couponAmount,
        browserInfo: getBrowserInfo(),
    };
    if (giftCardCode) {
        paymentData.gift_card_code = giftCardCode;
    }
    if (charityOrganisationId) {
        paymentData.charity_organisation_id = charityOrganisationId;
    }
    if (professionalData) {
        paymentData.professionalData = professionalData;
    }

    return postPayWithDebit(paymentUuid, paymentData);
}

export function payWithAvailableCredit(
    paymentUuid,
    { giftCardCode, charityOrganisationId, professionalData }
) {
    const options = {};
    if (giftCardCode) {
        options.gift_card_code = giftCardCode;
    }
    if (charityOrganisationId) {
        options.charity_organisation_id = charityOrganisationId;
    }
    if (professionalData) {
        options.professionalData = professionalData;
    }
    return postPaymentCoupon(paymentUuid, options).then(function filterSucceededFromFailed(data) {
        if (data.paymentStatus !== SUCCEEDED_STATUS) {
            throw new Error('Payment error');
        }
        return data;
    });
}

export function cancelPayment(url, method) {
    return jqueryToPromise($.ajax(process.env.API_ROOT + url, { method }));
}

export function retryPayment(paymentUuid) {
    return postRepayOrder(paymentUuid);
}

export function checkGiftCard(giftCardCode, orderId) {
    return postCheckGiftCard({
        code: giftCardCode,
        orderId,
    });
}

export const createGiftCardPaymentAnonymous = params => createGiftCardPaymentAnonymousAPI(params);
